import { analyticsApis } from "@services/apis/Analytics";
import { GetBalanceSumResponse } from "@services/models/Prime/GetBalanceSumModel";
import { GetUnsweptBalanceModel } from "@services/models/Prime/GetUnsweptBalanceModel";
import { useEffect, useState } from "react";

const BalanceSum = () => {
  const [personalBalanceSum, setPersonalBalanceSum] = useState<number>(0);
  const [merchantBalanceSum, setMerchantBalanceSum] = useState<number>(0);
  const [personalUnsweptBalance, setPersonalUnsweptBalance] =
    useState<number>(0);
  const [merchantUnsweptBalance, setMerchantUnsweptBalance] =
    useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const getPersonalBalanceSum = async () => {
    try {
      const response: GetBalanceSumResponse =
        await analyticsApis.getPersonalBalanceSum();
      const totalBalance = response.data.reduce(
        (sum, item) => sum + item.balanceSum,
        0,
      );
      setPersonalBalanceSum(totalBalance);
    } catch (error: any) {
      console.error("Error fetching personal balance:", error);
    }
  };

  const getMerchantBalanceSum = async () => {
    try {
      const response: GetBalanceSumResponse =
        await analyticsApis.getMerchantBalanceSum();
      const totalBalance = response.data.reduce(
        (sum, item) => sum + item.balanceSum,
        0,
      );
      setMerchantBalanceSum(totalBalance);
    } catch (error: any) {
      console.error("Error fetching merchant balance:", error);
    }
  };

  const getMerchantUnsweptBalance = async () => {
    try {
      const response: GetUnsweptBalanceModel =
        await analyticsApis.getMerchantUnsweptBalance();
      const totalBalance = response.data.unsweptBalances.reduce(
        (sum, item) => sum + (item.merchantUnsweptAmount || 0),
        0,
      );
      setMerchantUnsweptBalance(totalBalance);
    } catch (error: any) {
      console.error("Error fetching merchant unswept balance:", error);
    }
  };

  const getPersonalUnsweptBalance = async () => {
    try {
      const response: GetUnsweptBalanceModel =
        await analyticsApis.getPersonalUnsweptBalance();
      const totalBalance = response.data.unsweptBalances.reduce(
        (sum, item) => sum + (item.personalUnsweptAmount || 0),
        0,
      );
      setPersonalUnsweptBalance(totalBalance);
    } catch (error: any) {
      console.error("Error fetching personal unswept balance:", error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        getPersonalBalanceSum(),
        getMerchantBalanceSum(),
        getMerchantUnsweptBalance(),
        getPersonalUnsweptBalance(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  return (
    <div className="mb-4 w-full">
      <div className="flex flex-row justify-between items-center mb-3">
        <h2 className="text-[14px] font-semibold">Balance Summary</h2>
        <div className="flex rounded-full border border-bg-button-secondary bg-bg-button-secondary cursor-pointer p-1">
          <span
            className={`material-symbols-outlined text-text-base text-[16px] font-semibold ${
              loading ? "animate-spin" : ""
            }`}
            onClick={fetchAllData}
          >
            refresh
          </span>
        </div>
      </div>

      <div className="border border-border-subtle overflow-hidden">
        {/* Table Header */}
        <div className="flex flex-row bg-bg-base">
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle"></div>
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle text-center">
            Personal
          </div>
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle text-center">
            Merchant
          </div>
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] text-center">
            Total
          </div>
        </div>

        {/* Normal Balance Row */}
        <div className="flex flex-row border-t border-border-subtle">
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle bg-bg-page-body">
            Normal Balance
          </div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center">
            {personalBalanceSum.toFixed(2)}
          </div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center">
            {merchantBalanceSum.toFixed(2)}
          </div>
          <div className="w-[25%] text-[10px] p-[10px] text-center font-medium">
            {(merchantBalanceSum + personalBalanceSum).toFixed(2)}
          </div>
        </div>

        {/* Unswept Balance Row */}
        <div className="flex flex-row border-t border-border-subtle">
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle bg-bg-page-body">
            Unswept Balance
          </div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center">
            {personalUnsweptBalance.toFixed(2)}
          </div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center">
            {merchantUnsweptBalance.toFixed(2)}
          </div>
          <div className="w-[25%] text-[10px] p-[10px] text-center font-medium">
            {(merchantUnsweptBalance + personalUnsweptBalance).toFixed(2)}
          </div>
        </div>

        {/* Total Row */}
        <div className="flex flex-row border-t border-border-subtle bg-bg-page-body">
          <div className="w-[25%] font-semibold text-[10px] text-gray-700 p-[10px] border-r border-border-subtle">
            Total
          </div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center font-bold"></div>
          <div className="w-[25%] text-[10px] p-[10px] border-r border-border-subtle text-center font-bold"></div>
          <div className="w-[25%] text-[10px] p-[10px] text-center font-bold">
            {(
              merchantBalanceSum +
              personalBalanceSum -
              (merchantUnsweptBalance + personalUnsweptBalance)
            ).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSum;
